import { useCallback, useMemo } from "react";
import { PageContent } from "@src/presentation/components/Layout/common";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import {
  OrganizationsHeader,
  OrganizationsBrandList,
  OrganizationsFooter,
  OrganizationsTwoColumnLayout,
  OrganizationCard,
  useUpdateBrandStatus,
} from "./utils";
import "./Organizations.scss";
import {
  useFetchOrganizationsData,
  useOrganizationsData,
} from "@src/presentation/providers/OrganizationsDataProvider";
import { GetOrganizationsDTO } from "@src/domain/dto/organizations";
import { OrganizationRepository } from "@src/infrastructure/repositories/organization";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { GetOrganizationsUseCase } from "@src/application/useCases/organization";
import { LoadingSpinner } from "@src/presentation/components/common";

export const OrganizationsPage = () => {
  const {
    data,
    loading,
    error,
    currentPage,
    itemsPerPage,
    setCurrentPage,
    applyOrganizationCreate,
  } = useOrganizationsData();
  const handleUpdateBrandStatus = useUpdateBrandStatus();

  const fetchDeps = useMemo(
    () => [currentPage, itemsPerPage],
    [currentPage, itemsPerPage],
  );

  useFetchOrganizationsData(
    useCallback(
      async (options?: { refetch?: boolean }) => {
        const payload = {
          page_number: currentPage,
          page_size: itemsPerPage,
        };
        const validated = GetOrganizationsDTO.validate(payload);
        if (!validated.success) {
          console.error("GetOrganizations validation failed", validated.error);
          throw new Error("GetOrganizations validation failed");
        }
        const repo = new OrganizationRepository(
          GraphQlAdapter.getInstance().getClient(),
        );
        const dto = new GetOrganizationsDTO(validated.data);
        const result = await new GetOrganizationsUseCase(repo).execute(
          dto,
          options?.refetch,
        );

        return result;
      },
      [currentPage, itemsPerPage],
    ),
    fetchDeps,
  );

  const organizations = data?.items ?? [];
  const totalOrganizations = data?.total ?? 0;
  const activeOrganizationCount = organizations.filter(
    (org) =>
      org.organizationIsActive === true && org.organizationDeletedAt === null,
  ).length;
  const activeBrandCount = organizations.reduce(
    (sum, org) =>
      sum +
      (org.brands ?? []).filter(
        (b) => b.brandIsActive === true && b.brandDeletedAt === null,
      ).length,
    0,
  );

  const handlePageChangeOrganizations = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <ErrorBoundary>
      <OrganizationsHeader
        loading={loading}
        activeOrganizationCount={activeOrganizationCount}
        activeBrandCount={activeBrandCount}
        error={error}
        onOrganizationSaved={applyOrganizationCreate}
      />
      <PageContent className="p-4">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <OrganizationsTwoColumnLayout>
            {organizations.map((organization) => {
              const orgBrands = organization.brands ?? [];
              const totalOrgBrands = orgBrands.length;
              const brandsToShow = totalOrgBrands > 4 ? orgBrands.slice(0, 4) : orgBrands;
              return (
                <OrganizationCard
                  key={organization.organizationId}
                  organization={organization}
                  showFooter={totalOrgBrands > 4}
                >
                  <OrganizationsBrandList
                    brands={brandsToShow}
                    onUpdateBrandStatus={handleUpdateBrandStatus}
                    onUpdateSuccess={() => {}}
                    organization={organization}
                  />
                </OrganizationCard>
              );
            })}
          </OrganizationsTwoColumnLayout>
        )}
      </PageContent>
      {totalOrganizations > itemsPerPage && (
        <OrganizationsFooter
          totalOrganizations={totalOrganizations}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChangeOrganizations}
          loading={loading}
        />
      )}
    </ErrorBoundary>
  );
};
